import { SetupPhase, SetupStep } from '@core/models';

/**
 * The screens a setup step can open.
 *
 * Authored steps used to take a route as free text — "/company" — which only
 * someone who knows the router could fill in, and a typo gave a button that led
 * nowhere. This is the list a person picks from, kept in step with app.routes.ts.
 * The default button wording lives here too, so picking a screen fills it in.
 */
export const SETUP_SCREENS: ReadonlyArray<{ route: string; label: string; openLabel: string }> = [
  { route: '/company', label: 'Company', openLabel: 'Open company' },
  { route: '/company/settings', label: 'Company Settings', openLabel: 'Open company settings' },
  { route: '/business-model', label: 'Business Model', openLabel: 'Open business model' },
  { route: '/operating-systems', label: 'Operating Systems', openLabel: 'Open operating systems' },
  { route: '/org-chart', label: 'Org Chart', openLabel: 'Open org chart' },
  { route: '/people', label: 'People', openLabel: 'Open people' },
  { route: '/career-pathways', label: 'Career Pathways', openLabel: 'Set pathways' },
  { route: '/training', label: 'Training Modules', openLabel: 'Open training' },
  { route: '/checklists', label: 'Checklists', openLabel: 'Build checklists' },
  { route: '/projects', label: 'Projects', openLabel: 'Open projects' },
  { route: '/proj-scheduling', label: 'Scheduling', openLabel: 'Open scheduling' },
  { route: '/project-settings', label: 'Project Settings', openLabel: 'Open project settings' },
  { route: '/opportunities', label: 'Opportunities', openLabel: 'Open opportunities' },
  { route: '/onboarding', label: 'Onboarding', openLabel: 'Open onboarding' },
  { route: '/handbook', label: 'Handbook', openLabel: 'Open handbook' },
  { route: '/users', label: 'Users & Access', openLabel: 'Open users' },
  { route: '/command', label: 'Command Center', openLabel: 'Open command center' },
];

/**
 * The icons a step can wear. A curated set with plain-English names, because
 * "pi-building" is a PrimeIcon class and nobody authoring a checklist knows one.
 */
export const SETUP_ICONS: ReadonlyArray<{ icon: string; label: string }> = [
  { icon: 'pi-building', label: 'Building' },
  { icon: 'pi-sitemap', label: 'Org chart' },
  { icon: 'pi-users', label: 'People' },
  { icon: 'pi-user', label: 'Person' },
  { icon: 'pi-star', label: 'Star' },
  { icon: 'pi-chart-line', label: 'Growth' },
  { icon: 'pi-book', label: 'Book' },
  { icon: 'pi-list', label: 'List' },
  { icon: 'pi-check-square', label: 'Checklist' },
  { icon: 'pi-th-large', label: 'Projects' },
  { icon: 'pi-calendar', label: 'Calendar' },
  { icon: 'pi-briefcase', label: 'Briefcase' },
  { icon: 'pi-file', label: 'Document' },
  { icon: 'pi-folder', label: 'Folder' },
  { icon: 'pi-cog', label: 'Settings' },
  { icon: 'pi-shield', label: 'Shield' },
  { icon: 'pi-dollar', label: 'Money' },
  { icon: 'pi-clock', label: 'Time' },
  { icon: 'pi-map-marker', label: 'Location' },
  { icon: 'pi-flag', label: 'Flag' },
];

/**
 * The seeded groups. The heading's NUMBER is not stored — it is the group's
 * position, rendered as "1 · Set the foundation", so reordering renumbers by
 * itself instead of leaving an author to retype every heading.
 */
export const SETUP_PHASES_SEED: readonly SetupPhase[] = [
  { id: 'foundation', name: 'Set the foundation' },
  { id: 'define', name: 'Define the work' },
  { id: 'motion', name: 'Put it in motion' },
];

/**
 * The seeded Get Started checklist — what "setting up Diamond Ridge" consists of.
 *
 * These were hardcoded in the Get Started component. They are the STARTING point
 * now, not the definition: an admin edits, adds and reorders them in the app, and
 * their edits replace this.
 */
export const SETUP_STEPS_SEED: readonly SetupStep[] = [
  {
    id: 'company',
    phaseId: 'foundation',
    icon: 'pi-building',
    title: 'Your company information',
    why: 'Mission, vision, core values, founded date, headquarters, the 25-year growth plan, and your welcome video.',
    metric: 'divisions',
    statusText: '',
    route: '/company',
    openLabel: 'Open company',
  },
  {
    id: 'os',
    phaseId: 'foundation',
    icon: 'pi-star',
    title: 'Your operating systems',
    why: 'The repeatable process each division runs on (CMOS, PMOS…) — the phases and steps every project moves through.',
    metric: 'operatingSystems',
    statusText: '',
    route: '/operating-systems',
    openLabel: 'Open operating systems',
  },
  {
    id: 'people',
    phaseId: 'foundation',
    icon: 'pi-sitemap',
    title: 'Your people & org chart',
    why: 'Add your team and set who reports to whom. Everyone in the system starts here.',
    metric: 'peopleWithManager',
    statusText: '',
    route: '/people',
    openLabel: 'Open people',
  },
  {
    id: 'pathways',
    phaseId: 'foundation',
    icon: 'pi-chart-line',
    title: 'Career pathways',
    why: 'Define how each role grows into the next, so every person has a path to progress.',
    metric: 'pathways',
    statusText: '',
    route: '/career-pathways',
    openLabel: 'Set pathways',
  },
  {
    id: 'training',
    phaseId: 'define',
    icon: 'pi-book',
    title: 'Training & competencies',
    why: 'Set what each role must learn and master — the basis for assignments and sign-off.',
    metric: 'none',
    statusText: 'Competency catalog ready to review',
    route: '/training',
    openLabel: 'Open training',
  },
  {
    id: 'checklists',
    phaseId: 'define',
    icon: 'pi-list',
    title: 'Checklists',
    why: 'The recurring daily and weekly checklists that keep the work consistent.',
    metric: 'checklists',
    statusText: '',
    route: '/checklists',
    openLabel: 'Build checklists',
  },
  {
    id: 'projects',
    phaseId: 'define',
    icon: 'pi-th-large',
    title: 'Your projects',
    why: 'Enter your active and pipeline jobs — contract, cost, dates, and the team on each. This drives the dashboards, schedule, and spend.',
    metric: 'projects',
    statusText: '',
    route: '/projects',
    openLabel: 'Open projects',
  },
  {
    id: 'workforce',
    phaseId: 'define',
    icon: 'pi-users',
    title: 'Your labor pool',
    why: 'Set your trades and headcount so the system can flag where you’ll be short crews. (Projects → Workforce.)',
    metric: 'none',
    statusText: 'Set trades & headcount',
    route: '/projects',
    openLabel: 'Open projects',
  },
  {
    id: 'onboarding',
    phaseId: 'motion',
    icon: 'pi-check-square',
    title: 'Onboard your people',
    why: 'Run new hires through onboarding so nothing gets missed on day one.',
    metric: 'onboarding',
    statusText: '',
    route: '/onboarding',
    openLabel: 'Open onboarding',
  },
];
